/**
 * Admin Incidents dashboard — /admin/incidents.
 *
 * Workflow:
 *   An admin sees every incident in a filterable list (tabs by status,
 *   plus category/priority filters), opens one to view the full timeline,
 *   and takes action: start review, forward to a department manager,
 *   take an immediate action (resolve), close or reject. A categories
 *   manager dialog lets the admin tweak the 8 default classes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Siren, Clock, UserMinus, CheckCircle, XCircle, PaperPlaneTilt,
  Warning, Tag, Flame, Fire, ListChecks, Plus, PencilSimple, Trash,
  Paperclip, Download, ChatCircleText, FunnelSimple, CaretLeft,
} from '@phosphor-icons/react';

import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const STATUS_META = {
  submitted:    { ar: 'جديد',          en: 'New',         cls: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800',    Icon: Siren },
  under_review: { ar: 'قيد المراجعة',  en: 'Under review',cls: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800',  Icon: Clock },
  assigned:     { ar: 'محوّل',         en: 'Forwarded',   cls: 'bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800',Icon: PaperPlaneTilt },
  in_progress:  { ar: 'قيد التنفيذ',   en: 'In progress', cls: 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:border-violet-800',Icon: Clock },
  resolved:     { ar: 'تم اتخاذ إجراء',en: 'Resolved',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800',Icon: CheckCircle },
  closed:       { ar: 'مغلق',           en: 'Closed',      cls: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',   Icon: CheckCircle },
  rejected:     { ar: 'مرفوض',          en: 'Rejected',    cls: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:border-rose-800',      Icon: XCircle },
};

const PRIORITY_META = {
  urgent: { ar: 'عاجل',   en: 'Urgent', cls: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800',   Icon: Fire },
  high:   { ar: 'مرتفع',  en: 'High',   cls: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800', Icon: Flame },
  medium: { ar: 'متوسط',  en: 'Medium', cls: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:border-amber-800', Icon: Warning },
  low:    { ar: 'منخفض',  en: 'Low',    cls: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700', Icon: Tag },
};

const EVENT_LABELS = {
  submitted:          { ar: 'تم تقديم البلاغ', en: 'Report submitted' },
  under_review:       { ar: 'بدأت المراجعة',    en: 'Review started' },
  forwarded:          { ar: 'تم التحويل',       en: 'Forwarded' },
  'status:in_progress': { ar: 'قيد التنفيذ',    en: 'In progress' },
  'status:resolved':   { ar: 'تم اتخاذ إجراء',   en: 'Resolved' },
  'status:closed':     { ar: 'تم الإغلاق',       en: 'Closed' },
  'status:rejected':   { ar: 'تم الرفض',         en: 'Rejected' },
  comment:             { ar: 'تعليق جديد',       en: 'Comment added' },
};

export default function AdminIncidents() {
  const navigate = useNavigate();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const [params, setParams] = useSearchParams();

  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [managers, setManagers] = useState([]);
  const [statusTab, setStatusTab] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(null); // 'resolve' | 'close' | 'reject'
  const [forwardAssignee, setForwardAssignee] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [actionResolution, setActionResolution] = useState('');
  const [commentText, setCommentText] = useState('');
  const [commenting, setCommenting] = useState(false);

  const T = (ar, en) => (isAr ? ar : en);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (statusTab !== 'all') q.status = statusTab;
      if (filterPriority !== 'all') q.priority = filterPriority;
      if (filterCategory !== 'all') q.category_key = filterCategory;
      const res = await axios.get(`${API}/incidents`, { params: q, withCredentials: true });
      setIncidents(res.data.incidents || []);
      setStats(res.data.stats || {});
    } catch (e) {
      toast.error(T('تعذّر تحميل البلاغات', 'Failed to load incidents'));
    } finally {
      setLoading(false);
    }
  }, [statusTab, filterPriority, filterCategory, isAr]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/incidents/categories`, { withCredentials: true });
      setCategories(res.data.categories || []);
    } catch (e) { /* noop */ }
  }, []);

  const fetchManagers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees`, { withCredentials: true });
      const eligible = (res.data || []).filter(
        (e) => e.is_department_manager || e.role === 'admin' || e.role === 'general_manager' || e.is_general_manager
      );
      setManagers(eligible);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);
  useEffect(() => { fetchCategories(); fetchManagers(); }, [fetchCategories, fetchManagers]);

  // Deep-link: ?focus=<id> opens the detail dialog directly.
  useEffect(() => {
    const focus = params.get('focus');
    if (focus && focus !== detailId) setDetailId(focus);
  }, [params, detailId]);

  const openDetail = useCallback(async (id) => {
    setDetailId(id);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/incidents/${id}`, { withCredentials: true });
      setDetail(res.data);
    } catch (e) {
      toast.error(T('تعذّر عرض البلاغ', 'Failed to open incident'));
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [isAr]);

  useEffect(() => { if (detailId) openDetail(detailId); }, [detailId, openDetail]);

  const closeDetail = () => {
    setDetailId(null); setDetail(null);
    setForwardOpen(false); setActionOpen(null);
    setForwardAssignee(''); setForwardNote(''); setActionNote(''); setActionResolution('');
    setCommentText('');
    const next = new URLSearchParams(params);
    next.delete('focus');
    setParams(next, { replace: true });
  };

  const refresh = async () => {
    await fetchIncidents();
    if (detailId) await openDetail(detailId);
  };

  const act = async (path, payload, successMsg) => {
    try {
      await axios.post(`${API}/incidents/${detailId}/${path}`, payload, { withCredentials: true });
      toast.success(successMsg);
      await refresh();
      setForwardOpen(false); setActionOpen(null);
      setForwardAssignee(''); setForwardNote(''); setActionNote(''); setActionResolution('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || T('فشل الإجراء', 'Action failed'));
    }
  };

  const submitComment = async () => {
    if (!commentText.trim()) return;
    setCommenting(true);
    try {
      await axios.post(`${API}/incidents/${detailId}/comment`, { text: commentText.trim() }, { withCredentials: true });
      setCommentText('');
      await refresh();
    } catch (e) {
      toast.error(T('تعذّر إرسال التعليق', 'Failed to add comment'));
    } finally {
      setCommenting(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return incidents;
    return incidents.filter((i) =>
      (i.reference || '').toLowerCase().includes(q) ||
      (i.title || '').toLowerCase().includes(q) ||
      (i.reporter?.name || '').toLowerCase().includes(q) ||
      (i.reporter?.name_en || '').toLowerCase().includes(q) ||
      (i.reporter?.employee_number || '').toLowerCase().includes(q)
    );
  }, [incidents, search]);

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(isAr ? 'ar-SA' : 'en-GB', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  const isOverdue = (inc) => {
    if (!inc.due_at || ['closed', 'rejected'].includes(inc.status)) return false;
    try { return new Date(inc.due_at).getTime() < Date.now(); } catch { return false; }
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA] dark:bg-slate-950" dir={direction} data-testid="admin-incidents-page">
      {/* Header — standard admin header: logo on one side, Back button on the other */}
      <header className="sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] dark:border-slate-700 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <Button variant="ghost" onClick={() => navigate('/home')} className="text-[#655dc6] hover:bg-[#EDE9FE] dark:hover:bg-slate-800" data-testid="back-home-btn">
            <CaretLeft size={18} className={isAr ? 'ms-1 rotate-180' : 'me-1'} />
            {T('رجوع', 'Back')}
          </Button>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Title + categories action */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
              <Siren size={28} weight="duotone" className="text-rose-600 dark:text-rose-400" />
              {T('بلاغات الحوادث', 'Incident Reports')}
            </h1>
            <p className="text-sm text-[#655dc6]/70 dark:text-slate-400 mt-1">
              {T('إدارة البلاغات والتصنيف والتحويل ومتابعة الحالة لحظياً.', 'Analyse, categorise, forward and resolve incidents with a live timeline.')}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowCatMgr(true)}
            className="border-[#C4BFF0] dark:border-slate-700 text-[#655dc6] dark:text-violet-300 hover:bg-[#EDE9FE] dark:hover:bg-slate-800 gap-2 shrink-0"
            data-testid="categories-manager-btn"
          >
            <Tag size={16} weight="duotone" />
            {T('إدارة التصنيفات', 'Categories')}
          </Button>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <StatCard icon={Siren} tint="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" label={T('بلاغات جديدة', 'New')} value={stats.submitted || 0} data-testid="stat-submitted" />
          <StatCard icon={Clock} tint="bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" label={T('قيد المعالجة', 'In progress')} value={(stats.under_review || 0) + (stats.assigned || 0) + (stats.in_progress || 0)} data-testid="stat-progress" />
          <StatCard icon={CheckCircle} tint="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" label={T('مُغلقة', 'Closed')} value={stats.closed || 0} data-testid="stat-closed" />
          <StatCard icon={Warning} tint="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300" label={T('تجاوزت SLA', 'Overdue')} value={stats.overdue || 0} data-testid="stat-overdue" />
        </div>

        {/* Filters */}
        <Card className="border-[#C4BFF0] dark:border-slate-700 dark:bg-slate-900">
          <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-2">
            <Input
              placeholder={T('بحث عن مرجع، عنوان، أو موظف…', 'Search ref / title / employee…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-72 bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
              data-testid="incidents-search"
            />
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-full sm:w-40 bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100" data-testid="filter-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{T('كل الأولويات', 'All priorities')}</SelectItem>
                {Object.entries(PRIORITY_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{T(m.ar, m.en)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-full sm:w-56 bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100" data-testid="filter-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{T('كل التصنيفات', 'All categories')}</SelectItem>
                {categories.filter(c => c.active).map(c => (
                  <SelectItem key={c.key} value={c.key}>{T(c.name_ar, c.name_en)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={statusTab} onValueChange={setStatusTab} dir={direction}>
          <TabsList className="bg-white dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 w-full sm:w-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="all" data-testid="tab-all" className="shrink-0 gap-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">
              <ListChecks size={14} /> {T('الكل', 'All')}
            </TabsTrigger>
            {['submitted', 'under_review', 'assigned', 'in_progress', 'resolved', 'closed', 'rejected'].map((s) => {
              const m = STATUS_META[s];
              const Icon = m.Icon;
              return (
                <TabsTrigger key={s} value={s} data-testid={`tab-${s}`} className="shrink-0 gap-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">
                  <Icon size={14} /> {T(m.ar, m.en)}
                  {(stats[s] || 0) > 0 && <span className="text-[10px] bg-[#655dc6]/10 dark:bg-slate-700 text-[#655dc6] dark:text-violet-300 px-1.5 rounded-full ml-1">{stats[s]}</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {/* List */}
        <Card className="border-[#C4BFF0] dark:border-slate-700 dark:bg-slate-900">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-2">
              <FunnelSimple size={16} /> {filtered.length} {T('بلاغ', 'incidents')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center py-12 text-[#655dc6]/60 dark:text-slate-400 text-sm">
                {T('لا توجد بلاغات تطابق البحث', 'No incidents match your filters')}
              </p>
            ) : (
              <div className="space-y-3" data-testid="incidents-list">
                {filtered.map((inc) => {
                  const sm = STATUS_META[inc.status] || STATUS_META.submitted;
                  const pm = PRIORITY_META[inc.priority] || PRIORITY_META.medium;
                  const StatusIcon = sm.Icon;
                  const PrIcon = pm.Icon;
                  const overdue = isOverdue(inc);
                  return (
                    <button
                      type="button"
                      key={inc.id}
                      onClick={() => openDetail(inc.id)}
                      className="w-full text-start bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 hover:border-[#655dc6] hover:shadow-sm transition"
                      data-testid={`incident-row-${inc.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <span className="font-mono text-[11px] text-[#655dc6]/80 dark:text-slate-300 bg-[#E8E6F5] dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 px-1.5 py-0.5 rounded">
                              {inc.reference}
                            </span>
                            <Badge variant="outline" className={`${sm.cls} text-[10px] gap-1`}>
                              <StatusIcon size={10} weight="bold" />
                              {T(sm.ar, sm.en)}
                            </Badge>
                            <Badge variant="outline" className={`${pm.cls} text-[10px] gap-1`}>
                              <PrIcon size={10} weight="bold" />
                              {T(pm.ar, pm.en)}
                            </Badge>
                            {overdue && (
                              <Badge className="bg-red-600 text-white text-[10px] gap-1">
                                <Warning size={10} weight="fill" />
                                {T('تجاوز SLA', 'Overdue')}
                              </Badge>
                            )}
                          </div>
                          <h4 className="text-sm sm:text-base font-semibold text-[#262263] dark:text-slate-100 truncate">{inc.title}</h4>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-[#655dc6]/80 dark:text-slate-400">
                        <div>
                          <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-500">{T('المُبلّغ', 'Reporter')}</span>
                          <span className="font-medium text-slate-700 dark:text-slate-200">{inc.reporter?.name || '—'}</span>
                        </div>
                        <div>
                          <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-500">{T('التصنيف', 'Category')}</span>
                          <span className="font-medium text-slate-700 dark:text-slate-200">{T(inc.category?.name_ar, inc.category?.name_en) || '—'}</span>
                        </div>
                        <div>
                          <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-500">{T('المسؤول', 'Assignee')}</span>
                          <span className="font-medium text-slate-700 dark:text-slate-200">{inc.assignee?.name || T('—', '—')}</span>
                        </div>
                        <div>
                          <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-500">{T('آخر تحديث', 'Last update')}</span>
                          <span className="font-medium text-slate-700 dark:text-slate-200">{fmt(inc.last_update_at)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Detail dialog */}
      <IncidentDetailDialog
        detailId={detailId}
        detail={detail}
        loading={detailLoading}
        isAr={isAr}
        onClose={closeDetail}
        managers={managers}
        forwardOpen={forwardOpen} setForwardOpen={setForwardOpen}
        forwardAssignee={forwardAssignee} setForwardAssignee={setForwardAssignee}
        forwardNote={forwardNote} setForwardNote={setForwardNote}
        actionOpen={actionOpen} setActionOpen={setActionOpen}
        actionNote={actionNote} setActionNote={setActionNote}
        actionResolution={actionResolution} setActionResolution={setActionResolution}
        onAct={act}
        onComment={submitComment}
        commentText={commentText} setCommentText={setCommentText}
        commenting={commenting}
      />

      {/* Categories manager */}
      <CategoriesDialog
        open={showCatMgr}
        onOpenChange={setShowCatMgr}
        categories={categories}
        refresh={fetchCategories}
        isAr={isAr}
      />
    </div>
  );
}

function StatCard({ icon: Icon, tint, label, value, 'data-testid': testid }) {
  return (
    <Card className="border-[#C4BFF0] dark:border-slate-700 dark:bg-slate-900" data-testid={testid}>
      <CardContent className="p-3 sm:p-4 flex items-center gap-3">
        <div className={`${tint} rounded-xl w-10 h-10 flex items-center justify-center shrink-0`}>
          <Icon size={22} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] text-[#655dc6]/70 dark:text-slate-400 truncate">{label}</div>
          <div className="text-xl font-bold text-[#262263] dark:text-slate-100">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function IncidentDetailDialog({
  detailId, detail, loading, isAr, onClose,
  managers,
  forwardOpen, setForwardOpen, forwardAssignee, setForwardAssignee,
  forwardNote, setForwardNote,
  actionOpen, setActionOpen, actionNote, setActionNote,
  actionResolution, setActionResolution,
  onAct, onComment, commentText, setCommentText, commenting,
}) {
  const T = (ar, en) => (isAr ? ar : en);
  const open = Boolean(detailId);
  const inc = detail?.incident;
  const events = detail?.events || [];
  const canReview = inc?.status === 'submitted';
  const canForward = inc && !['closed', 'rejected'].includes(inc.status);
  const canResolve = inc && ['submitted', 'under_review', 'assigned', 'in_progress'].includes(inc.status);
  const canClose = inc?.status === 'resolved';
  const canReject = inc && ['submitted', 'under_review'].includes(inc.status);

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(isAr ? 'ar-SA' : 'en-GB',
      { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const sm = inc ? (STATUS_META[inc.status] || STATUS_META.submitted) : null;
  const pm = inc ? (PRIORITY_META[inc.priority] || PRIORITY_META.medium) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-slate-900 dark:border-slate-700" data-testid="incident-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base dark:text-slate-100">
            <Siren size={20} weight="duotone" className="text-rose-600 dark:text-rose-400" />
            {inc?.reference || T('تفاصيل البلاغ', 'Incident details')}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
          </div>
        )}

        {!loading && inc && (
          <div className="space-y-4">
            {/* Status row */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={`${sm.cls} gap-1`}>
                <sm.Icon size={12} weight="bold" />
                {T(sm.ar, sm.en)}
              </Badge>
              <Badge variant="outline" className={`${pm.cls} gap-1`}>
                <pm.Icon size={12} weight="bold" />
                {T(pm.ar, pm.en)}
              </Badge>
              <Badge variant="outline" className="bg-[#E8E6F5] text-[#655dc6] border-[#C4BFF0] dark:bg-slate-800 dark:text-violet-300 dark:border-slate-700">
                {T(inc.category?.name_ar, inc.category?.name_en)}
              </Badge>
            </div>

            {/* Reporter + assignee */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoBlock label={T('المُبلّغ', 'Reporter')}
                        name={inc.reporter?.name}
                        meta={`#${inc.reporter?.employee_number || '—'} · ${inc.reporter?.department || ''}`}
              />
              <InfoBlock label={T('المسؤول الحالي', 'Current assignee')}
                        name={inc.assignee?.name || T('لم يُحوَّل بعد', 'Not forwarded yet')}
                        meta={inc.assignee ? `#${inc.assignee.employee_number || '—'} · ${inc.assignee.department || ''}` : ''}
              />
            </div>

            {/* Core fields */}
            <div className="rounded-lg border border-[#C4BFF0] dark:border-slate-700 p-3 bg-[#F8F6FF] dark:bg-slate-800/60">
              <h3 className="font-semibold text-[#262263] dark:text-slate-100 text-sm">{inc.title}</h3>
              <p className="text-xs text-[#655dc6]/80 dark:text-slate-300 mt-1 whitespace-pre-wrap">{inc.description}</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-[#655dc6]/80 dark:text-slate-300 mt-3">
                <div>
                  <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('تاريخ الحادث', 'Incident date')}</span>
                  <span className="font-medium">{inc.incident_date || '—'}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('الموقع', 'Location')}</span>
                  <span className="font-medium">{inc.location || '—'}</span>
                </div>
                <div className="col-span-2">
                  <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('الشهود', 'Witnesses')}</span>
                  <span className="font-medium">{inc.witnesses || '—'}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('تاريخ التقديم', 'Submitted')}</span>
                  <span className="font-medium tabular-nums">{fmt(inc.submitted_at)}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('موعد SLA', 'SLA due')}</span>
                  <span className="font-medium tabular-nums">{fmt(inc.due_at)}</span>
                </div>
                {inc.resolution && (
                  <div className="col-span-2 pt-2 border-t border-[#C4BFF0] dark:border-slate-700 mt-1">
                    <span className="block text-[10px] text-[#655dc6]/60 dark:text-slate-400">{T('الإجراء المتخذ', 'Resolution')}</span>
                    <span className="font-medium whitespace-pre-wrap">{inc.resolution}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            {inc.attachments?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-1.5">
                  <Paperclip size={14} /> {T('المرفقات', 'Attachments')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {inc.attachments.map((a, idx) => (
                    <a
                      key={idx}
                      href={`${API}/incidents/${inc.id}/attachment/${idx}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 bg-[#E8E6F5] dark:bg-slate-800 border border-[#C4BFF0] dark:border-slate-700 text-[#655dc6] dark:text-violet-300 text-xs px-2 py-1 rounded hover:bg-[#DAD5F0] dark:hover:bg-slate-700"
                      data-testid={`attachment-link-${idx}`}
                    >
                      <Download size={12} /> {a.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#C4BFF0] dark:border-slate-700">
              {canReview && (
                <Button
                  size="sm"
                  onClick={() => onAct('review', {}, T('بدأت المراجعة', 'Review started'))}
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-1"
                  data-testid="start-review-btn"
                >
                  <Clock size={14} /> {T('بدء المراجعة', 'Start review')}
                </Button>
              )}
              {canForward && (
                <Button
                  size="sm"
                  onClick={() => setForwardOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
                  data-testid="forward-btn"
                >
                  <PaperPlaneTilt size={14} /> {T('تحويل', 'Forward')}
                </Button>
              )}
              {canResolve && (
                <Button
                  size="sm"
                  onClick={() => setActionOpen('resolve')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                  data-testid="resolve-btn"
                >
                  <CheckCircle size={14} /> {T('تم اتخاذ إجراء', 'Resolve')}
                </Button>
              )}
              {canClose && (
                <Button
                  size="sm"
                  onClick={() => setActionOpen('close')}
                  className="bg-slate-700 hover:bg-slate-800 text-white gap-1"
                  data-testid="close-btn"
                >
                  <CheckCircle size={14} /> {T('إغلاق', 'Close')}
                </Button>
              )}
              {canReject && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActionOpen('reject')}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 gap-1"
                  data-testid="reject-btn"
                >
                  <XCircle size={14} /> {T('رفض', 'Reject')}
                </Button>
              )}
            </div>

            {/* Timeline */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-1.5">
                <ChatCircleText size={14} /> {T('مسار البلاغ', 'Timeline')}
              </h4>
              <div className="space-y-2" data-testid="incident-timeline">
                {events.map((e) => {
                  const label = EVENT_LABELS[e.kind] || { ar: e.kind, en: e.kind };
                  return (
                    <div key={e.id} className="bg-white dark:bg-slate-800/60 border border-[#C4BFF0] dark:border-slate-700 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-[#262263] dark:text-slate-100">{T(label.ar, label.en)}</span>
                        <span className="text-[10px] text-[#655dc6]/60 dark:text-slate-400 tabular-nums">{fmt(e.created_at)}</span>
                      </div>
                      <div className="text-[11px] text-[#655dc6]/80 dark:text-slate-300">
                        {T(`بواسطة ${e.by_user_name || '—'}`, `by ${e.by_user_name || '—'}`)}
                      </div>
                      {e.note && (
                        <p className="text-xs text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap">{e.note}</p>
                      )}
                      {e.kind === 'forwarded' && e.meta?.assignee_name && (
                        <p className="text-[11px] text-indigo-700 dark:text-indigo-300 mt-1">
                          → {e.meta.assignee_name} ({e.meta.assignee_department || ''})
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* New comment */}
              <div className="pt-2 border-t border-[#C4BFF0] dark:border-slate-700">
                <Textarea
                  rows={2}
                  placeholder={T('اكتب تعليقاً للمُبلّغ أو المسؤول…', 'Add a comment for the reporter / assignee…')}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 text-sm"
                  data-testid="comment-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={onComment}
                    disabled={commenting || !commentText.trim()}
                    className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-1"
                    data-testid="submit-comment-btn"
                  >
                    <ChatCircleText size={14} /> {T('إرسال', 'Post')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Forward sub-dialog */}
      <Dialog open={forwardOpen} onOpenChange={setForwardOpen}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="dark:bg-slate-900 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">{T('تحويل البلاغ لمدير قسم', 'Forward to a department manager')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={forwardAssignee} onValueChange={setForwardAssignee}>
              <SelectTrigger className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100" data-testid="forward-assignee-select">
                <SelectValue placeholder={T('اختر مديراً', 'Select a manager')} />
              </SelectTrigger>
              <SelectContent>
                {managers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} {m.department ? `· ${m.department}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              rows={3}
              placeholder={T('تعليمات أو ملاحظات له (اختياري)…', 'Instructions (optional)…')}
              value={forwardNote}
              onChange={(e) => setForwardNote(e.target.value)}
              className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
              data-testid="forward-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForwardOpen(false)}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={() => onAct('forward', { assignee_user_id: forwardAssignee, note: forwardNote }, T('تم التحويل', 'Forwarded'))}
              disabled={!forwardAssignee}
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1"
              data-testid="forward-confirm-btn"
            >
              <PaperPlaneTilt size={14} /> {T('تحويل', 'Forward')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action sub-dialog */}
      <Dialog open={Boolean(actionOpen)} onOpenChange={(v) => !v && setActionOpen(null)}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="dark:bg-slate-900 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">
              {actionOpen === 'resolve' && T('تسجيل الإجراء المتخذ', 'Record action taken')}
              {actionOpen === 'close' && T('إغلاق البلاغ', 'Close incident')}
              {actionOpen === 'reject' && T('رفض البلاغ', 'Reject incident')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {actionOpen !== 'close' && (
              <Textarea
                rows={4}
                placeholder={T('اشرح الإجراء المتخذ…', 'Describe the action taken…')}
                value={actionResolution}
                onChange={(e) => setActionResolution(e.target.value)}
                className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
                data-testid="action-resolution"
              />
            )}
            <Textarea
              rows={2}
              placeholder={T('ملاحظة إضافية (اختياري)', 'Internal note (optional)')}
              value={actionNote}
              onChange={(e) => setActionNote(e.target.value)}
              className="bg-[#E8E6F5] dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500"
              data-testid="action-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionOpen(null)}>{T('إلغاء', 'Cancel')}</Button>
            <Button
              onClick={() => {
                if (actionOpen === 'resolve') onAct('action', { status: 'resolved', note: actionNote, resolution: actionResolution }, T('تم تسجيل الإجراء', 'Action recorded'));
                if (actionOpen === 'close')  onAct('action', { status: 'closed', note: actionNote }, T('تم الإغلاق', 'Closed'));
                if (actionOpen === 'reject') onAct('action', { status: 'rejected', note: actionNote, resolution: actionResolution }, T('تم الرفض', 'Rejected'));
              }}
              disabled={actionOpen !== 'close' && !actionResolution.trim()}
              className={
                actionOpen === 'resolve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : actionOpen === 'close' ? 'bg-slate-700 hover:bg-slate-800 text-white'
                : 'bg-rose-600 hover:bg-rose-700 text-white'
              }
              data-testid="action-confirm-btn"
            >
              {T('تأكيد', 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function InfoBlock({ label, name, meta }) {
  return (
    <div className="rounded-lg border border-[#C4BFF0] dark:border-slate-700 p-3 bg-white dark:bg-slate-800/60">
      <div className="text-[10px] text-[#655dc6]/60 dark:text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="font-semibold text-sm text-[#262263] dark:text-slate-100">{name || '—'}</div>
      {meta && <div className="text-[11px] text-[#655dc6]/70 dark:text-slate-400 mt-0.5">{meta}</div>}
    </div>
  );
}

function CategoriesDialog({ open, onOpenChange, categories, refresh, isAr }) {
  const T = (ar, en) => (isAr ? ar : en);
  const [key, setKey] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!key.trim() || !nameAr.trim() || !nameEn.trim()) {
      toast.error(T('عبّئ الحقول الثلاثة', 'Fill all three fields'));
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/incidents/categories`, {
        key: key.trim().toLowerCase(),
        name_ar: nameAr.trim(),
        name_en: nameEn.trim(),
      }, { withCredentials: true });
      toast.success(T('تمت الإضافة', 'Added'));
      setKey(''); setNameAr(''); setNameEn('');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || T('فشل الإنشاء', 'Create failed'));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(T('حذف هذا التصنيف؟', 'Delete this category?'))) return;
    try {
      await axios.delete(`${API}/incidents/categories/${id}`, { withCredentials: true });
      toast.success(T('تم الحذف', 'Deleted'));
      await refresh();
    } catch (e) { toast.error(T('فشل الحذف', 'Delete failed')); }
  };

  const toggleActive = async (id, active) => {
    try {
      await axios.put(`${API}/incidents/categories/${id}`, { active: !active }, { withCredentials: true });
      await refresh();
    } catch (e) { toast.error(T('فشل التحديث', 'Update failed')); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl dark:bg-slate-900 dark:border-slate-700" dir={isAr ? 'rtl' : 'ltr'} data-testid="categories-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
            <Tag size={18} /> {T('إدارة تصنيفات البلاغات', 'Incident categories')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-[#C4BFF0] dark:border-slate-700 p-3 space-y-2 bg-[#F8F6FF] dark:bg-slate-800/60">
            <h4 className="text-xs font-semibold text-[#655dc6] dark:text-violet-300 flex items-center gap-1.5">
              <Plus size={14} /> {T('إضافة تصنيف جديد', 'Add new category')}
            </h4>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder={T('مفتاح (eg: fire)', 'key')} value={key} onChange={(e) => setKey(e.target.value)}
                     className="bg-white dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 text-sm" data-testid="new-cat-key" />
              <Input placeholder={T('اسم بالعربية', 'Arabic name')} value={nameAr} onChange={(e) => setNameAr(e.target.value)}
                     className="bg-white dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 text-sm" data-testid="new-cat-ar" />
              <Input placeholder={T('اسم بالإنجليزية', 'English name')} value={nameEn} onChange={(e) => setNameEn(e.target.value)}
                     className="bg-white dark:bg-slate-800 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-100 dark:placeholder:text-slate-500 text-sm" data-testid="new-cat-en" />
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={add} disabled={busy} className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-1" data-testid="add-category-btn">
                <Plus size={14} /> {T('إضافة', 'Add')}
              </Button>
            </div>
          </div>

          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="categories-list">
            {categories.map((c) => (
              <div key={c.id} className={`flex items-center justify-between gap-2 border rounded-lg p-2 ${c.active ? 'bg-white dark:bg-slate-800/60 border-[#C4BFF0] dark:border-slate-700' : 'bg-slate-50 dark:bg-slate-800/30 border-slate-200 dark:border-slate-700 opacity-70'}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#262263] dark:text-slate-100">{T(c.name_ar, c.name_en)}</div>
                  <div className="text-[10px] text-[#655dc6]/60 dark:text-slate-400 font-mono">{c.key}</div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(c.id, c.active)}
                          className="text-xs px-2 h-8 border-[#C4BFF0] dark:border-slate-700 dark:text-slate-200" data-testid={`toggle-cat-${c.key}`}>
                    {c.active ? T('إيقاف', 'Disable') : T('تفعيل', 'Enable')}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c.id)} className="h-8 w-8 text-red-600 dark:text-red-400" data-testid={`del-cat-${c.key}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
